import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { OpsTask, OpsCategory, CRMUser, TaskStatus, TaskPriority } from '../../types';
import OpsTaskRow from './OpsTaskRow';
import { ChevronDown, ChevronRight, Plus, ArrowUpDown, Layers, List, Search, X } from 'lucide-react';
import './OpsTable.css';

const CATEGORY_LABELS: Record<OpsCategory, string> = {
  admin: 'Admin',
  maintenance: 'Maintenance',
  ordering: 'Ordering',
  requests: 'Requests',
  other: 'Other',
};

const CATEGORY_COLORS: Record<OpsCategory, string> = {
  admin: '#6366F1',
  maintenance: '#F59E0B',
  ordering: '#0891B2',
  requests: '#8B5CF6',
  other: '#6B7280',
};

const PRIORITY_ORDER: Record<TaskPriority, number> = {
  urgent: 0, high: 1, medium: 2, low: 3,
};

type SortField = 'default' | 'priority' | 'due' | 'status';
type SortDir = 'asc' | 'desc';

interface OpsTableProps {
  tasks: OpsTask[];
  teamMembers: CRMUser[];
  onSelectTask: (task: OpsTask) => void;
  onUpdateTask: (id: string, updates: Partial<OpsTask>) => void;
  onCreateTask: (data: Partial<OpsTask>) => void | Promise<string | undefined>;
  /** When filters hide every task but the hub still has tasks loaded. */
  emptyFilteredMessage?: string;
}

function sortTasks(tasks: OpsTask[], field: SortField, dir: SortDir): OpsTask[] {
  const sorted = [...tasks];

  const onHoldPartition = (arr: OpsTask[]) => {
    const active = arr.filter(t => t.status !== 'on-hold');
    const held = arr.filter(t => t.status === 'on-hold');
    return [...active, ...held];
  };

  if (field === 'default') return onHoldPartition(sorted);

  sorted.sort((a, b) => {
    let cmp = 0;
    if (field === 'priority') {
      cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
    } else if (field === 'due') {
      const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      cmp = aDate - bDate;
    } else if (field === 'status') {
      cmp = a.status.localeCompare(b.status);
    }
    return dir === 'desc' ? -cmp : cmp;
  });

  return onHoldPartition(sorted);
}

const OpsTable: React.FC<OpsTableProps> = ({ tasks, teamMembers, onSelectTask, onUpdateTask, onCreateTask, emptyFilteredMessage }) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [addingInGroup, setAddingInGroup] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [sortField, setSortField] = useState<SortField>('default');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [flatMode, setFlatMode] = useState(false);
  const [taskSearch, setTaskSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const displayTasks = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) => {
      const title = (t.title || '').toLowerCase();
      const desc = (t.description || '').toLowerCase();
      const tags = (t.tags || []).join(' ').toLowerCase();
      return title.includes(q) || desc.includes(q) || tags.includes(q);
    });
  }, [tasks, taskSearch]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const flatSorted = useMemo(() => {
    return sortTasks(displayTasks, sortField, sortDir);
  }, [displayTasks, sortField, sortDir]);

  const grouped = useMemo(() => {
    const map = new Map<OpsCategory, OpsTask[]>();
    for (const cat of Object.keys(CATEGORY_LABELS) as OpsCategory[]) {
      map.set(cat, []);
    }
    for (const t of displayTasks) {
      const arr = map.get(t.category);
      if (arr) arr.push(t);
    }
    map.forEach((catTasks, cat) => {
      map.set(cat, sortTasks(catTasks, sortField, sortDir));
    });
    return map;
  }, [displayTasks, sortField, sortDir]);

  const toggleGroup = useCallback((cat: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  const handleQuickAdd = async (category: OpsCategory) => {
    const title = newTitle.trim();
    if (!title) return;
    setQuickAddError(null);
    try {
      await onCreateTask({
        title,
        category,
        status: 'pending' as TaskStatus,
        priority: 'medium' as TaskPriority,
        startDate: new Date(),
        dueDate: new Date(Date.now() + 7 * 86400000),
      });
      setNewTitle('');
      setAddingInGroup(null);
    } catch (err) {
      console.error('Error creating ops task:', err);
      setQuickAddError('Could not save task. Try again.');
    }
  };

  const nonEmpty = useMemo(() => {
    const result: [OpsCategory, OpsTask[]][] = [];
    grouped.forEach((catTasks, cat) => {
      if (catTasks.length > 0) result.push([cat, catTasks]);
    });
    return result;
  }, [grouped]);

  const clearSearch = useCallback(() => {
    setTaskSearch('');
    setSearchOpen(false);
  }, []);

  const SortBtn: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <button
      className={`ops-th-sort ${sortField === field ? 'active' : ''}`}
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field && (
        <span className={`ops-th-sort-arrow ${sortDir}`}>
          <ArrowUpDown size={10} />
        </span>
      )}
    </button>
  );

  if (tasks.length === 0) {
    return (
      <div className="ops-table-empty">
        <p>{emptyFilteredMessage ? 'No matching tasks' : 'No tasks yet'}</p>
        <span>
          {emptyFilteredMessage || 'Create your first task to get started'}
        </span>
      </div>
    );
  }

  return (
    <div className="ops-table pm-scroll">
      <div className="ops-table-header">
        <div className="ops-th ops-th-title">
          <span className="ops-th-title-label">Task</span>
          <div className="ops-th-title-tools">
            <button
              type="button"
              className={`ops-search-toggle ${searchOpen || taskSearch.trim() ? 'active' : ''}`}
              onClick={() => {
                setSearchOpen(true);
                requestAnimationFrame(() => searchInputRef.current?.focus());
              }}
              title="Search tasks"
              aria-label="Search tasks"
              aria-expanded={searchOpen || !!taskSearch.trim()}
            >
              <Search size={11} strokeWidth={2.25} />
            </button>
            {(searchOpen || taskSearch.trim()) && (
              <div className="ops-table-search-field">
                <input
                  ref={searchInputRef}
                  type="search"
                  className="ops-table-search-input"
                  placeholder="Search…"
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                  onBlur={() => {
                    window.setTimeout(() => {
                      if (!taskSearch.trim()) setSearchOpen(false);
                    }, 180);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') clearSearch();
                  }}
                  aria-label="Filter tasks by title, description, or tags"
                />
                {taskSearch.trim() ? (
                  <button
                    type="button"
                    className="ops-table-search-clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={clearSearch}
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <X size={10} strokeWidth={2.5} />
                  </button>
                ) : null}
              </div>
            )}
            <button
              className={`ops-flat-toggle ${flatMode ? 'active' : ''}`}
              onClick={() => setFlatMode(!flatMode)}
              title={flatMode ? 'Group by category' : 'Flat list (sorted)'}
              aria-label={flatMode ? 'Group by category' : 'Flat list sorted'}
            >
              {flatMode ? <Layers size={11} /> : <List size={11} />}
            </button>
          </div>
        </div>
        <div className="ops-th ops-th-status"><SortBtn field="status" label="Status" /></div>
        <div className="ops-th ops-th-priority"><SortBtn field="priority" label="Priority" /></div>
        <div className="ops-th ops-th-assignee">Assignee</div>
        <div className="ops-th ops-th-due"><SortBtn field="due" label="Due" /></div>
        <div className="ops-th ops-th-action">Action</div>
      </div>

      {taskSearch.trim() && tasks.length > 0 && displayTasks.length === 0 ? (
        <div className="ops-table-search-empty">
          <p>No tasks match &ldquo;{taskSearch.trim()}&rdquo;</p>
          <button type="button" className="ops-table-search-empty-clear" onClick={clearSearch}>
            Clear search
          </button>
        </div>
      ) : flatMode ? (
        /* ── Flat sorted list ── */
        <div className="ops-table-flat">
          {flatSorted.map((task, i) => (
            <div key={task.id} className="ops-flat-row-wrap">
              <OpsTaskRow
                task={task}
                teamMembers={teamMembers}
                onSelect={() => onSelectTask(task)}
                onUpdate={(updates) => onUpdateTask(task.id!, updates)}
                animDelay={i * 20}
                showCategory
              />
            </div>
          ))}
        </div>
      ) : (
        /* ── Grouped by category ── */
        nonEmpty.map(([cat, catTasks]) => {
          const isCollapsed = collapsed.has(cat);
          return (
            <div key={cat} className="ops-table-group">
              <div className="ops-table-group-header" onClick={() => toggleGroup(cat)}>
                <span className="ops-table-group-color" style={{ background: CATEGORY_COLORS[cat] }} />
                <span className="ops-table-group-toggle">
                  {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                </span>
                <span className="ops-table-group-name">{CATEGORY_LABELS[cat]}</span>
                <span className="ops-table-group-count">{catTasks.length}</span>
              </div>
              {!isCollapsed && (
                <>
                  {catTasks.map((task, i) => (
                    <OpsTaskRow
                      key={task.id}
                      task={task}
                      teamMembers={teamMembers}
                      onSelect={() => onSelectTask(task)}
                      onUpdate={(updates) => onUpdateTask(task.id!, updates)}
                      animDelay={i * 30}
                    />
                  ))}
                  {addingInGroup === cat ? (
                    <div className="ops-table-add-row">
                      <input
                        className="ops-table-add-input"
                        placeholder="Task title..."
                        value={newTitle}
                        onChange={e => { setNewTitle(e.target.value); if (quickAddError) setQuickAddError(null); }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleQuickAdd(cat);
                          }
                          if (e.key === 'Escape') {
                            setAddingInGroup(null);
                            setNewTitle('');
                            setQuickAddError(null);
                          }
                        }}
                        autoFocus
                      />
                      {quickAddError ? (
                        <span className="ops-table-add-error" role="alert">{quickAddError}</span>
                      ) : null}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="ops-table-add-btn"
                      onClick={() => { setAddingInGroup(cat); setQuickAddError(null); }}
                    >
                      <Plus size={12} /> Add task
                    </button>
                  )}
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default OpsTable;
